import { useCallback, useEffect, useState } from 'react'
import { format, formatISO } from 'date-fns'

import metricsService from 'services/metrics'

import {
  SCHEDULED,
  RETURNED,
  PROFESSIONAL_NO_SHOW,
  ATTENDED,
  PATIENT_NO_SHOW,
  UNATTENDED,
} from 'settings/_scheduleStatusSettings'

import { 
  IAvailableAvailabilities,
  IFormattedScheduleAvailabilities, 
  IHoursViewData, 
  IScheduledAvailabilities, 
  IUseDataParams 
} from '../types'

const statusList = [
  SCHEDULED,
  ATTENDED,
  UNATTENDED,
  PATIENT_NO_SHOW,
  PROFESSIONAL_NO_SHOW,
  RETURNED,
]

const hours = {
  '00:00': {},
  '01:00': {},
  '02:00': {},
  '03:00': {},
  '04:00': {},
  '05:00': {},
  '06:00': {},
  '07:00': {},
  '08:00': {},
  '09:00': {},
  '10:00': {},
  '11:00': {},
  '12:00': {},
  '13:00': {},
  '14:00': {},
  '15:00': {},
  '16:00': {},
  '17:00': {},
  '18:00': {},
  '19:00': {},
  '20:00': {},
  '21:00': {},
  '22:00': {},
  '23:00': {},
}

function sortSlotsByHour(data: IFormattedScheduleAvailabilities[]) {
  return data.sort((a, b) => {
    if (a.hour < b.hour) {
      return -1
    }
    if (a.hour > b.hour) {
      return 1
    }
    return 0
  })
}

function reduceDataToCorrectHour(data: IFormattedScheduleAvailabilities[]) {
  const allSlots = Object.keys(hours).reduce((acc, curr) => {
    const slot = data.find(slot => slot.hour === curr)
    
    if (slot) {
      acc[curr] = slot
    }

    if(!slot) {
      acc[curr] = {
        ...slot,
        hour: curr,
      }
    }
    return acc as IFormattedScheduleAvailabilities[]
  }, [])

  return sortSlotsByHour(Object.values(allSlots))
}

function notFoundData() {
  return Object.keys(hours).map((hourKey) => {
    return {
      hour: hourKey,
      value: null,
      percentage: null
    }
  })
}

function calculatePercentage(partialValue: number, totalValue: number) {
  if (!partialValue) return null

  const percentage = (partialValue / totalValue) * 100
  return percentage.toFixed(1)
}

function reduceTotalSlotsAvailability(data: IFormattedScheduleAvailabilities[]) {
  return data.reduce((acc, curr) => acc + curr.value, 0)
}

function reduceSlotsToCalculatePercentage(data: IFormattedScheduleAvailabilities[]) {
  const total = reduceTotalSlotsAvailability(data)
  const slotsWithPercentage = data.map(slot => {
    return {
      ...slot,
      percentage: calculatePercentage(slot.value, total)
    }
  })

  return reduceDataToCorrectHour(slotsWithPercentage)
}

const formatScheduledSlots = (scheduledHours: IScheduledAvailabilities) => {
  return Object.keys(hours).map((hourKey) => {
    const filteredSlot = scheduledHours.times.filter(slot => slot.time.substring(0, 2) === hourKey.substring(0, 2))

    if (filteredSlot.length) {
      return {
        hour: hourKey,
        value: filteredSlot.reduce((acc, curr) => acc + curr.partial, 0),
        percentage: filteredSlot.reduce((acc, curr) => acc + curr.percentage, 0)
      }
    }
    
    return {
      hour: hourKey,
      value: null,
      percentage: null
    }
  })
}

function formatData(availableHours: IAvailableAvailabilities[], scheduledHours: IScheduledAvailabilities) {
  const availableSlots = availableHours.map(slot => {
    return {
      hour: slot.time?.replace(':00', ''),
      value: slot?.availabilities_count,
    }
  })

  const formattedScheduledSlots = formatScheduledSlots(scheduledHours)

  const checkedAvailableSlotWithSchedule = availableSlots.map(slot => {
    const filteredSlot = formattedScheduledSlots.find(hour => hour.hour === slot.hour)
    if (filteredSlot) {
      if (slot.value > 0 && filteredSlot.value > 0) {
        return {
          ...filteredSlot,
          value: filteredSlot.value,
          percentage: filteredSlot.percentage
        }
      } else if (slot.value === 0) {
        return {
          ...filteredSlot,
          value: null,
          percentage: null
        }
      } else {
        return {
          ...filteredSlot,
          value: 0,
          percentage: 0
        }
      }
    }

    return {
      ...filteredSlot,
      value: 0,
      percentage: 0
    }
  })

  return reduceSlotsToCalculatePercentage(checkedAvailableSlotWithSchedule)
}

export const useHoursViewData = ({selectedDate}: IUseDataParams): IHoursViewData => {
  const [loadingHoursGraphic, setLoadingHoursGraphic] = useState(false)
  const [hoursViewData, setHoursViewData] = useState([])

  const fetchData = useCallback(async () => {
    const availableHoursResponse = await metricsService.getScheduleAvailabilities({
      initialDate: format(new Date(selectedDate.initialDate), 'yyyy-MM-dd'),
      endDate: format(new Date(selectedDate.endDate), 'yyyy-MM-dd'),
      type: 'time'
    })

    const scheduledHoursResponse = await metricsService.getListSchedules({
      initialDate: formatISO(new Date(selectedDate.initialDate)),
      endDate: formatISO(new Date(selectedDate.endDate)),
      groupBy: 'time',
      statusList
    })

    return Promise.all([availableHoursResponse, scheduledHoursResponse])
  }, [selectedDate])

  useEffect(() => {
    async function getScheduleSlotsList() {
      try{
        setLoadingHoursGraphic(true)
       
        const [availableHoursResponse, scheduledHoursResponse] = await fetchData()

        setHoursViewData(formatData(availableHoursResponse, scheduledHoursResponse.data))

      } catch (error) {
        if(error === 404) {
          setHoursViewData(notFoundData())
        }

        setLoadingHoursGraphic(false)
      } finally {
        setLoadingHoursGraphic(false)
      }
    }
    
    void getScheduleSlotsList()
  }, [selectedDate, fetchData])

  return {
    hoursViewData,
    loadingHoursGraphic
  }
}